import { PAYMENT_LABELS, PAYMENT_TAGS, type PaymentTag } from '@atlas-control/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';
import { PageHeader } from '@/components/page-header';
import { Pagination } from '@/components/pagination';
import { StatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formsQuery } from '@/features/forms/api';
import { PaymentBadge, ReviewBadge } from '@/features/forms/badges';
import { RunStatusBadge } from '@/features/runs/components';
import { formatDateTime, formatRelative } from '@/lib/format';
import { useCan } from '@/lib/auth';
import { guard } from '@/lib/guards';
import { pageSearch } from '@/lib/page-search';

const Search_ = z.object({
  payment: z.enum(PAYMENT_TAGS).optional(),
  reviewed: z.enum(['yes', 'no']).optional(),
  hasRun: z.enum(['yes', 'no']).optional(),
  q: z.string().optional(),
  page: pageSearch,
});

export const Route = createFileRoute('/_app/forms/')({
  beforeLoad: ({ context }) => guard(context.me, 'forms.read'),
  validateSearch: Search_,
  component: FormsPage,
});

const ALL = '__all__';

function FormsPage() {
  const { page = 1, ...search } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const list = useQuery(formsQuery(search, page));
  const canCreate = useCan('forms.create');
  const forms = list.data?.forms ?? [];
  const setPage = useCallback(
    (n: number) => void navigate({ search: (prev) => ({ ...prev, page: n > 1 ? n : undefined }) }),
    [navigate],
  );
  const [q, setQ] = useState(search.q ?? '');

  // Debounced search box → URL.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((search.q ?? '') !== q)
        void navigate({ search: (prev) => ({ ...prev, q: q || undefined, page: undefined }) });
    }, 300);
    return () => clearTimeout(t);
  }, [q, search.q, navigate]);

  const set = (patch: Partial<z.infer<typeof Search_>>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: undefined }) });

  return (
    <>
      <PageHeader
        title="Forms"
        actions={
          canCreate && (
            <Button asChild>
              <Link to="/forms/new">
                <Plus /> New form
              </Link>
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="absolute top-2.5 left-2.5 size-4 text-muted-foreground" aria-hidden />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Company, contact or form id"
            className="pl-8"
            aria-label="Search forms"
          />
        </div>
        <FilterSelect
          label="Payment"
          value={search.payment}
          options={PAYMENT_TAGS.map((t) => [t, PAYMENT_LABELS[t]])}
          onChange={(v) => set({ payment: v as PaymentTag | undefined })}
        />
        <FilterSelect
          label="Intake review"
          value={search.reviewed}
          options={[
            ['no', 'Pending'],
            ['yes', 'Done'],
          ]}
          onChange={(v) => set({ reviewed: v as 'yes' | 'no' | undefined })}
        />
        <FilterSelect
          label="Run"
          value={search.hasRun}
          options={[
            ['no', 'Not started'],
            ['yes', 'Started'],
          ]}
          onChange={(v) => set({ hasRun: v as 'yes' | 'no' | undefined })}
        />
      </div>

      <Card className="py-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Received</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Industry</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Intake review</TableHead>
              <TableHead>Run</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.isPending &&
              Array.from({ length: 6 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-5" />
                  </TableCell>
                </TableRow>
              ))}
            {list.isSuccess && forms.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                  {Object.values(search).some(Boolean)
                    ? 'No forms match these filters.'
                    : 'No forms yet. Intakes appear here as they arrive.'}
                </TableCell>
              </TableRow>
            )}
            {forms.map((f) => (
              <TableRow
                key={f.id}
                className="cursor-pointer"
                onClick={() => void navigate({ to: '/forms/$formId', params: { formId: f.id } })}
              >
                <TableCell
                  className="whitespace-nowrap text-muted-foreground"
                  title={formatDateTime(f.receivedAt)}
                >
                  {formatRelative(f.receivedAt)}
                </TableCell>
                <TableCell className="font-medium">
                  <Link
                    to="/forms/$formId"
                    params={{ formId: f.id }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {f.company}
                  </Link>
                  {f.source === 'manual' && (
                    <StatusBadge tone="info" className="ml-2">
                      Manual
                    </StatusBadge>
                  )}
                </TableCell>
                <TableCell>
                  {f.contact.name || f.contact.email ? (
                    <div className="leading-tight">
                      {f.contact.name && <div>{f.contact.name}</div>}
                      {f.contact.email && (
                        <div className="text-xs text-muted-foreground">{f.contact.email}</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{f.industry || '-'}</TableCell>
                <TableCell>
                  <PaymentBadge tag={f.paymentTag} />
                </TableCell>
                <TableCell>
                  <ReviewBadge done={f.intakeReviewed} by={f.intakeReviewedBy?.name} />
                </TableCell>
                <TableCell>
                  {f.run ? (
                    <Link
                      to="/runs/$runId"
                      params={{ runId: f.run.id }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <RunStatusBadge status={f.run.status} />
                    </Link>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Pagination info={list.data} onPageChange={setPage} />
    </>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | undefined;
  options: [string, string][];
  onChange: (v: string | undefined) => void;
}) {
  return (
    <Select value={value ?? ALL} onValueChange={(v) => onChange(v === ALL ? undefined : v)}>
      <SelectTrigger className="w-44" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{label}: all</SelectItem>
        {options.map(([v, l]) => (
          <SelectItem key={v} value={v}>
            {label}: {l}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
